import React from 'react';
import ReactECharts from 'echarts-for-react';
import type { IncomeExpenseReportData, MessageButton, ChartDataPoint } from '../../../../types/chat-message';
import { ReportActionButtons } from './ReportActionButtons';
import './RevenueReport.scss';

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('vi-VN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount) + 'đ';
};

interface IncomeExpenseReportProps {
  data?: IncomeExpenseReportData;
  buttons?: MessageButton[];
  onButtonClick?: (name: string, id: number, isHidden?: boolean) => void;
  isLastMessage?: boolean;
  isFromHistory?: boolean;
  text?: string;
}

const MetricRow: React.FC<{ label: string; value: number; isHighlight?: boolean }> = ({
  label,
  value,
  isHighlight
}) => (
  <div className="rr-metric-row">
    <span className="rr-metric-label">{label}</span>
    <span className={`rr-metric-value ${isHighlight ? 'highlight' : ''}`}>
      {formatCurrency(value)}
    </span>
  </div>
);

const buildChartOption = (chartData: ChartDataPoint[]) => {
  const times = chartData.map(d => d.time);
  const currentValues = chartData.map(d => d.value);
  const lastPeriodValues = chartData.map(d => d.lastPeriodValue);

  return {
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => {
        if (Array.isArray(params)) {
          return params
            .map((p: any) => `${p.seriesName}: ${formatCurrency(p.value)}`)
            .join('<br/>');
        }
        return '';
      },
    },
    legend: {
      data: ['Kỳ này', 'Kỳ trước'],
      bottom: 0,
      padding: [0, 8],
      textStyle: { fontSize: 11 },
    },
    grid: {
      left: 8,
      right: 8,
      top: 8,
      bottom: 32,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: times,
      boundaryGap: false,
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: { fontSize: 10, rotate: 30 },
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: '#f3f4f6' } },
      axisLabel: {
        formatter: (value: number) => {
          if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
          if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
          return value.toString();
        },
        fontSize: 10,
      },
    },
    series: [
      {
        name: 'Kỳ này',
        data: currentValues,
        type: 'line',
        smooth: true,
        symbolSize: 4,
        lineStyle: { width: 2 },
        itemStyle: { color: '#8B5CF6' },
        areaStyle: { color: 'rgba(139, 92, 246, 0.08)' },
      },
      {
        name: 'Kỳ trước',
        data: lastPeriodValues,
        type: 'line',
        smooth: true,
        symbolSize: 4,
        lineStyle: { width: 2 },
        itemStyle: { color: '#60A5FA' },
        areaStyle: { color: 'rgba(96, 165, 250, 0.08)' },
      },
    ],
  };
};

export const IncomeExpenseReport: React.FC<IncomeExpenseReportProps> = ({
  data,
  buttons,
  onButtonClick,
  isLastMessage,
  isFromHistory,
  text
}) => {
  const {
    timeRange,
    totalCashIn = 0,
    totalCashOut = 0,
    remindCash = 0,
    chartDataDetails,
  } = data ?? {};

  const isButtonDisabled = !isLastMessage || !!isFromHistory;
  const isEmpty = totalCashIn === 0 && totalCashOut === 0;

  return (
    <div className="revenue-report">
      {!data || isEmpty ? (
        <div>
          <span className="rr-empty-text">{text || 'Không có dữ liệu cho kỳ báo cáo này'}</span>
        </div>
      ) : (
        <>
          <div className="rr-header">
            <span className="rr-title">{text || `BÁO CÁO THU CHI ${timeRange || ''}`}</span>
          </div>
          <div className="rr-metrics">
            <MetricRow label="Tổng thu" value={totalCashIn} />
            <MetricRow label="Tổng chi" value={totalCashOut} />
            <div className="rr-metric-row">
              <span className="rr-metric-label">Chênh lệch</span>
              <span className={`rr-metric-value ${remindCash >= 0 ? 'up' : ''}`}>
                {remindCash >= 0 ? '+' + formatCurrency(remindCash) : formatCurrency((remindCash))}
              </span>
            </div>
          </div>
        </>
      )}

      <ReportActionButtons
        buttons={buttons}
        isDisabled={isButtonDisabled}
        onButtonClick={onButtonClick}
        reportType="Báo cáo thu chi"
      />
    </div>
  );
};
